import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  InputAdornment,
  Avatar,
  Tooltip,
  LinearProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Description as WordIcon,
  Slideshow as PptIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  OpenInNew as OpenIcon,
  Search as SearchIcon,
  Folder as FolderIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import planningService from '../../services/planningService';

const DEPARTMENTS = [
  { value: 'all', label: 'Tất cả bộ phận', color: '#6366f1' },
  { value: 'marketing', label: 'Marketing', color: '#ec4899' },
  { value: 'social_media', label: 'Social Media', color: '#06b6d4' },
  { value: 'content_creator', label: 'Content Creator', color: '#f59e0b' },
];

const CATEGORIES = [
  { value: 'planning', label: 'Kế hoạch', icon: '📋' },
  { value: 'report', label: 'Báo cáo', icon: '📊' },
  { value: 'guideline', label: 'Hướng dẫn', icon: '📖' },
  { value: 'template', label: 'Template', icon: '📄' },
  { value: 'other', label: 'Khác', icon: '📁' },
];

const FileIconComponent = ({ fileType, size = 40 }) => {
  const iconType = planningService.getFileIcon(fileType);
  const iconProps = { sx: { fontSize: size } };
  
  const icons = {
    pdf: <PdfIcon {...iconProps} sx={{ ...iconProps.sx, color: '#ef4444' }} />,
    excel: <ExcelIcon {...iconProps} sx={{ ...iconProps.sx, color: '#22c55e' }} />,
    word: <WordIcon {...iconProps} sx={{ ...iconProps.sx, color: '#3b82f6' }} />,
    ppt: <PptIcon {...iconProps} sx={{ ...iconProps.sx, color: '#f97316' }} />,
    image: <ImageIcon {...iconProps} sx={{ ...iconProps.sx, color: '#a855f7' }} />,
    file: <FileIcon {...iconProps} sx={{ ...iconProps.sx, color: '#64748b' }} />,
  };
  
  return icons[iconType] || icons.file;
};

const PlanningStorage = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ department: '', category: '', search: '' });
  const [activeTab, setActiveTab] = useState(0);
  
  // Modal states
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [uploadFile, setUploadFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    department: 'all',
    category: 'planning',
    url: '',
    tags: [],
  });
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsResult, statsResult] = await Promise.all([
        planningService.getAll(filters),
        planningService.getStats(),
      ]);
      if (itemsResult.success) setItems(itemsResult.data);
      if (statsResult.success) setStats(statsResult.data);
    } catch (error) {
      showSnackbar('Lỗi khi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      department: 'all',
      category: 'planning',
      url: '',
      tags: [],
    });
    setUploadFile(null);
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      showSnackbar('Vui lòng chọn file', 'error');
      return;
    }
    
    setUploading(true);
    try {
      await planningService.uploadFile(uploadFile, formData);
      showSnackbar('Upload thành công!');
      setUploadOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAddLink = async () => {
    if (!formData.url) {
      showSnackbar('Vui lòng nhập URL', 'error');
      return;
    }
    
    try {
      await planningService.addLink(formData);
      showSnackbar('Đã thêm link!');
      setLinkOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi thêm link', 'error');
    }
  };

  const handleEdit = async () => {
    if (!selectedItem) return;
    try {
      await planningService.update(selectedItem._id, formData);
      showSnackbar('Đã cập nhật!');
      setEditOpen(false);
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi cập nhật', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc muốn xóa?')) return;
    try {
      await planningService.delete(id);
      showSnackbar('Đã xóa!');
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi xóa', 'error');
    }
  };

  const handleDownload = async (item) => {
    try {
      // Track download and get URL
      await planningService.getDownloadUrl(item._id);
      // Open the Cloudinary URL directly
      window.open(item.url, '_blank');
    } catch (error) {
      showSnackbar('Lỗi khi tải file', 'error');
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      department: item.department,
      category: item.category,
      url: item.url || '',
      tags: item.tags || [],
    });
    setEditOpen(true);
  };

  const getDepartmentColor = (dept) => {
    return DEPARTMENTS.find(d => d.value === dept)?.color || '#64748b';
  };

  const getCategoryIcon = (cat) => {
    return CATEGORIES.find(c => c.value === cat)?.icon || '📁';
  };

  // Filter items by tab
  const filteredItems = items.filter(item => {
    if (activeTab === 1) return item.type === 'file';
    if (activeTab === 2) return item.type === 'link';
    return true;
  });

  return (
    <Box>
      {/* Header Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>Tổng tài liệu</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
              {stats?.total || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>Files</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
              {stats?.files || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>Links</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
              {stats?.links || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>Lượt tải</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
              {stats?.totalDownloads || 0}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Tìm kiếm..."
            size="small"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Bộ phận</InputLabel>
            <Select
              value={filters.department}
              onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              label="Bộ phận"
            >
              <MenuItem value="">Tất cả</MenuItem>
              {DEPARTMENTS.map(d => (
                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Danh mục</InputLabel>
            <Select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              label="Danh mục"
            >
              <MenuItem value="">Tất cả</MenuItem>
              {CATEGORIES.map(c => (
                <MenuItem key={c.value} value={c.value}>{c.icon} {c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)' },
            }}
          >
            Upload File
          </Button>
          <Button
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={() => setLinkOpen(true)}
          >
            Thêm Link
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Tất cả (${items.length})`} />
        <Tab label={`Files (${items.filter(i => i.type === 'file').length})`} />
        <Tab label={`Links (${items.filter(i => i.type === 'link').length})`} />
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Items Grid */}
      <Grid container spacing={2}>
        {filteredItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
            <Card
              sx={{
                borderRadius: 3,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: item.type === 'link' ? '#3b82f6' : '#f1f5f9',
                      width: 48,
                      height: 48,
                      mr: 1.5,
                    }}
                  >
                    {item.type === 'link' ? (
                      <LinkIcon sx={{ color: 'white' }} />
                    ) : (
                      <FileIconComponent fileType={item.fileType} size={28} />
                    )}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getCategoryIcon(item.category)} {CATEGORIES.find(c => c.value === item.category)?.label}
                    </Typography>
                  </Box>
                </Box>

                {/* Description */}
                {item.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.description}
                  </Typography>
                )}

                {/* Tags & Department */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    label={DEPARTMENTS.find(d => d.value === item.department)?.label}
                    size="small"
                    sx={{
                      bgcolor: getDepartmentColor(item.department) + '20',
                      color: getDepartmentColor(item.department),
                      fontWeight: 500,
                      fontSize: '0.7rem',
                    }}
                  />
                  {item.type === 'file' && (
                    <Chip
                      label={planningService.formatFileSize(item.fileSize)}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>

                {/* Footer */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(item.createdAt), 'dd/MM/yyyy', { locale: vi })}
                  </Typography>
                  <Box>
                    {item.type === 'file' ? (
                      <Tooltip title="Tải xuống">
                        <IconButton size="small" onClick={() => handleDownload(item)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Mở link">
                        <IconButton size="small" onClick={() => window.open(item.url, '_blank')}>
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Sửa">
                      <IconButton size="small" onClick={() => openEditModal(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Xóa">
                      <IconButton size="small" color="error" onClick={() => handleDelete(item._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {filteredItems.length === 0 && !loading && (
          <Grid item xs={12}>
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <FolderIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Chưa có tài liệu nào
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Bắt đầu bằng cách upload file hoặc thêm link
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setUploadOpen(true)}
              >
                Thêm tài liệu
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Upload Modal */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon sx={{ color: '#667eea' }} />
            Upload File
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '2px dashed #e2e8f0',
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              mb: 3,
              bgcolor: uploadFile ? '#f0fdf4' : '#f8fafc',
              cursor: 'pointer',
              '&:hover': { borderColor: '#667eea' },
            }}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                setUploadFile(file);
                if (!formData.title) {
                  setFormData({ ...formData, title: file.name });
                }
              }}
            />
            {uploadFile ? (
              <>
                <FileIconComponent fileType={uploadFile.type} size={48} />
                <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                  {uploadFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {planningService.formatFileSize(uploadFile.size)}
                </Typography>
              </>
            ) : (
              <>
                <UploadIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                <Typography variant="body1">Click để chọn file</Typography>
                <Typography variant="caption" color="text.secondary">
                  PDF, Excel, Word, PowerPoint, Ảnh (max 50MB)
                </Typography>
              </>
            )}
          </Box>

          <TextField
            fullWidth
            label="Tiêu đề"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Mô tả"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Bộ phận</InputLabel>
                <Select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  label="Bộ phận"
                >
                  {DEPARTMENTS.map(d => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Danh mục</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Danh mục"
                >
                  {CATEGORIES.map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.icon} {c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => { setUploadOpen(false); resetForm(); }}>Hủy</Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!uploadFile || uploading}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {uploading ? 'Đang upload...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Link Modal */}
      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon sx={{ color: '#3b82f6' }} />
            Thêm Link
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="URL"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            placeholder="https://..."
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Tiêu đề"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Mô tả"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Bộ phận</InputLabel>
                <Select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  label="Bộ phận"
                >
                  {DEPARTMENTS.map(d => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Danh mục</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Danh mục"
                >
                  {CATEGORIES.map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.icon} {c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => { setLinkOpen(false); resetForm(); }}>Hủy</Button>
          <Button
            variant="contained"
            onClick={handleAddLink}
            disabled={!formData.url}
          >
            Thêm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chỉnh sửa</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Tiêu đề"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          {selectedItem?.type === 'link' && (
            <TextField
              fullWidth
              label="URL"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            fullWidth
            label="Mô tả"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Bộ phận</InputLabel>
                <Select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  label="Bộ phận"
                >
                  {DEPARTMENTS.map(d => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Danh mục</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Danh mục"
                >
                  {CATEGORIES.map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.icon} {c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setEditOpen(false)}>Hủy</Button>
          <Button variant="contained" onClick={handleEdit}>Lưu</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanningStorage;
